



import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { ArrowLeft, Save, Loader2, Search } from "lucide-react";

const PaymentForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Dropdown Data States
  const [plots, setPlots] = useState([]);
  
  // Advanced Member Search States
  const [memberSuggestions, setMemberSuggestions] = useState([]);
  const [activeMemberSearch, setActiveMemberSearch] = useState(false);
  const [selectedMemberName, setSelectedMemberName] = useState("");
  const [selectedMemberObj, setSelectedMemberObj] = useState(null);

  const [formData, setFormData] = useState({
    paymentId: null,
    memberId: "",
    plotId: "",
    receiptNo: "",
    amountPaid: "",
    paymentDate: new Date().toISOString().split("T")[0],
    paymentType: "Down Payment",
    paymentMethod: "Bank Transfer",
    transactionReference: "",
    bankName: "",
    status: "Completed",
    remarks: ""
  });

  useEffect(() => {
    fetchDropdownData();
    if (editId) {
      fetchPaymentData(editId);
    }
  }, [editId]);

  const fetchDropdownData = async () => {
    try {
      // 1. Initially get top 10 members as suggestions (Latest 10)
      const memRes = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_BASE}/GetSuggestions`);
      const memData = await memRes.json();
      setMemberSuggestions(memData || []);

      // 2. Fetch all plots for the filtering engine
      const plotRes = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.PLOT_GET_ALL}?pageSize=5000`);
      const plotData = await plotRes.json();
      setPlots(plotData.data || []);
    } catch (err) {
      console.error("Failed to load dropdowns", err);
    }
  };

  const fetchPaymentData = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.PAYMENT_BASE}/${id}`);
      const data = await res.json();
      if (res.ok && data) {
        setFormData({
          paymentId: data.paymentId,
          memberId: data.memberId || "",
          plotId: data.plotId || "",
          receiptNo: data.receiptNo || "",
          amountPaid: data.amountPaid || "",
          paymentDate: data.paymentDate ? data.paymentDate.split("T")[0] : "",
          paymentType: data.paymentType || "",
          paymentMethod: data.paymentMethod || "",
          transactionReference: data.transactionReference || "",
          bankName: data.bankName || "",
          status: data.status || "Completed",
          remarks: data.remarks || ""
        });

        if (data.member) {
          setSelectedMemberName(`${data.member.memberName} (${data.member.phoneNo})`);
          setSelectedMemberObj(data.member);
        }
      }
    } catch (err) {
      Swal.fire("Error", "Failed to load payment details", "error");
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMemberSearch = async (term) => {
    setSelectedMemberName(term);
    // If empty, fetch top 10 latest. If typing, search DB.
    const url = term.trim().length === 0 
        ? `${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_BASE}/GetSuggestions`
        : `${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_BASE}/GetSuggestions?searchTerm=${term}`;
    
    try {
        const memRes = await fetch(url);
        const memData = await memRes.json();
        setMemberSuggestions(memData || []);
        setActiveMemberSearch(true);
    } catch (e) {
        setMemberSuggestions([]);
    }
  };

  const selectMember = (member) => {
    setFormData(prev => ({ ...prev, memberId: member.memberId, plotId: "" })); 
    setSelectedMemberName(`${member.memberName} (${member.phoneNo})`);
    setSelectedMemberObj(member);
    setActiveMemberSearch(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.memberId || !formData.amountPaid || !formData.paymentDate) {
      return Swal.fire("Warning", "Please select a member and fill all required fields.", "warning");
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        plotId: formData.plotId || null,
        amountPaid: formData.amountPaid.toString()
      };

      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.PAYMENT_BASE}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (res.ok && result.success !== false) {
        Swal.fire("Success", "Payment saved successfully", "success").then(() => {
          navigate("/payments");
        });
      } else {
        Swal.fire("Error", result.message || "Failed to save payment", "error");
      }
    } catch (err) {
      Swal.fire("Error", "Server error while saving", "error");
    }
    setSaving(false);
  };

  const styles = {
    container: { padding: "40px", backgroundColor: "#f4f7fe", minHeight: "calc(100vh - 70px)", fontFamily: "'Inter', sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" },
    title: { fontSize: "28px", fontWeight: "800", color: "#1b2559", margin: 0 },
    backBtn: { background: "none", border: "none", display: "flex", alignItems: "center", gap: "8px", color: "#64748b", cursor: "pointer", fontSize: "14px", fontWeight: "600", padding: "0 0 10px 0" },
    card: { backgroundColor: "#fff", padding: "30px", borderRadius: "20px", boxShadow: "0 4px 20px rgba(0,0,0,0.03)" },
    formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" },
    formGroup: { display: "flex", flexDirection: "column", gap: "8px" },
    label: { fontSize: "14px", fontWeight: "600", color: "#1b2559" },
    input: { padding: "12px 15px", borderRadius: "10px", border: "1.5px solid #e2e8f0", fontSize: "14px", outline: "none", backgroundColor: "#f8fafc", width: "100%", boxSizing: "border-box" },
    select: { padding: "12px 15px", borderRadius: "10px", border: "1.5px solid #e2e8f0", fontSize: "14px", outline: "none", backgroundColor: "#f8fafc", cursor: "pointer", width: "100%" },
    saveBtn: { backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "14px 30px", borderRadius: "12px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px", marginTop: "30px" },
    sectionTitle: { fontSize: "16px", fontWeight: "700", color: "#1b2559", borderBottom: "2px solid #f1f5f9", paddingBottom: "10px", marginTop: "20px", marginBottom: "15px", gridColumn: "1 / -1" },
    suggestionBox: { position: "absolute", zIndex: 10000, background: "#fff", width: "100%", border: "1.5px solid #4f46e5", borderRadius: "12px", boxShadow: "0 15px 35px rgba(0,0,0,0.2)", maxHeight: "250px", overflowY: "auto", top: "75px", left: 0 },
    suggestionItem: { padding: "12px 15px", cursor: "pointer", borderBottom: "1px solid #f1f5f9", fontSize: "13.5px", color: "#1b2559", fontWeight: "700" }
  };

  if (loading) return <div style={styles.container}>Loading form...</div>;

  // ROBUST PLOT FILTERING: Checks both direct PlotId and PlotNumbers string
  const availablePlots = plots.filter(p => {
    if (!selectedMemberObj) return false;
    
    // 1. Check direct link if available
    if (selectedMemberObj.plotId && p.plotId === selectedMemberObj.plotId) return true;
    
    // 2. Check the comma-separated legacy string
    if (selectedMemberObj.plotNumbers) {
      const memberPlotsArray = selectedMemberObj.plotNumbers.split(',').map(s => s.trim().toLowerCase());
      return memberPlotsArray.includes(p.plotNumber?.toString().toLowerCase());
    }
    
    return false;
  });

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        <button style={styles.backBtn} onClick={() => navigate("/payments")}>
          <ArrowLeft size={16} /> Back to Payments
        </button>

        <div style={styles.header}>
          <h2 style={styles.title}>{editId ? "Edit Payment" : "Record New Payment"}</h2>
        </div>

        <div style={styles.card}>
          <form onSubmit={handleSubmit}>
            <div style={styles.formGrid}>
              <div style={styles.sectionTitle}>Link to Account</div>
              <div style={{ ...styles.formGroup, position: 'relative' }}>
                <label style={styles.label}>Member</label>
                <div style={{ position: 'relative' }}>
                  <Search size={16} style={{ position: 'absolute', right: 15, top: 14, color: '#a3aed0' }} />
                  <input 
                    style={styles.input} 
                    value={selectedMemberName} 
                    onChange={(e) => handleMemberSearch(e.target.value)} 
                    onFocus={() => setActiveMemberSearch(true)}
                    onBlur={() => setTimeout(() => setActiveMemberSearch(false), 200)}
                    placeholder="Search by Name or Phone..." 
                  />
                </div>
                {activeMemberSearch && memberSuggestions.length > 0 && (
                  <div style={styles.suggestionBox}>
                    {memberSuggestions.map((m) => (
                      <div key={m.memberId} style={styles.suggestionItem} onMouseDown={() => selectMember(m)}>
                        {m.memberName} ({m.phoneNo})
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Plots</label>
                <select name="plotId" value={formData.plotId} onChange={handleChange} style={styles.select}>
                  <option value="">-- No Specific Plot --</option>
                  {availablePlots.map(p => (
                    <option key={p.plotId} value={p.plotId}>{p.plotNumber} - {p.project?.projectName}</option>
                  ))}
                </select>
              </div>

              <div style={styles.sectionTitle}>Transaction Details</div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Receipt Number</label>
                <input type="text" name="receiptNo" value={formData.receiptNo} onChange={handleChange} style={styles.input} placeholder="e.g. REC-5001" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Amount(₹) *</label>
                <input type="number" name="amountPaid" value={formData.amountPaid} onChange={handleChange} style={styles.input} required placeholder="e.g. 50000" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Date *</label>
                <input type="date" name="paymentDate" value={formData.paymentDate} onChange={handleChange} style={styles.input} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Type *</label>
                <select name="paymentType" value={formData.paymentType} onChange={handleChange} style={styles.select} required>
                  <option value="Booking Amount">Booking Amount</option>
                  <option value="Down Payment">Down Payment</option>
                  <option value="Installment">Monthly Installment</option>
                  <option value="Late Fee">Late Fee / Penalty</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Method *</label>
                <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange} style={styles.select} required>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div style={styles.sectionTitle}>Bank & Tracking Info</div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Transaction Reference / Cheque No.</label>
                <input type="text" name="transactionReference" value={formData.transactionReference} onChange={handleChange} style={styles.input} placeholder="UTR / UPI ID / Cheque No" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Bank Name</label>
                <input type="text" name="bankName" value={formData.bankName} onChange={handleChange} style={styles.input} placeholder="e.g. HDFC, SBI" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Status *</label>
                <select name="status" value={formData.status} onChange={handleChange} style={styles.select} required>
                  <option value="Completed">Received</option>
                  <option value="Pending">Pending / Clearing</option>
                  <option value="Bounced">Bounced</option>
                  <option value="Failed">Failed</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Remarks / Notes</label>
                <input type="text" name="remarks" value={formData.remarks} onChange={handleChange} style={styles.input} placeholder="Any extra notes..." />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="submit" disabled={saving} style={styles.saveBtn}>
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} 
                {saving ? "Saving..." : "Save Payment"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default PaymentForm;